export const noop = () => {};

export function createListTransition<T extends object>(
  source: Accessor<readonly T[]>,
  options: ListTransitionOptions<T>
): Accessor<T[]> {
  const initSource = untrack(source);

  if (isServer) {
    const copy = initSource.slice();
    return () => copy;
  }

  const { onChange } = options;

  // if appear is enabled, the initial transition won't have any previous elements.
  // otherwise the elements will match and transition skipped, or transitioned if the source is different from the initial value
  let prevSet: ReadonlySet<T> = new Set(options.appear ? undefined : initSource);
  const exiting = new WeakSet<T>();

  const [toRemove, setToRemove] = createSignal<T[]>([], { equals: false });
  // const [isTransitionPending] = useTransition();

  const finishRemoved: (els: T[]) => void =
    options.exitMethod === 'remove'
      ? noop
      : (els) => {
          setToRemove((p) => (p.push.apply(p, els), p));
          for (const el of els) exiting.delete(el);
        };

  const handleRemoved: (els: T[], el: T, i: number) => void =
    options.exitMethod === 'remove'
      ? noop
      : options.exitMethod === 'keep-index'
        ? (els, el, i) => els.splice(i, 0, el)
        : (els, el) => els.push(el);

  return createMemo(
    (prev) => {
      const elsToRemove = toRemove();
      const sourceList = source();
      // (sourceList as any)[$TRACK]; // top level store tracking

      if (elsToRemove.length) {
        const next = prev.filter((e) => !elsToRemove.includes(e));
        elsToRemove.length = 0;
        onChange({
          list: next,
          added: [],
          removed: [],
          unchanged: next,
          finishRemoved,
        });
        return next;
      }

      return untrack(() => {
        const nextSet: ReadonlySet<T> = new Set(sourceList);
        const next: T[] = sourceList.slice();

        const added: T[] = [];
        const removed: T[] = [];
        const unchanged: T[] = [];

        for (const el of sourceList) {
          if (prevSet.has(el)) {
            unchanged.push(el);
          } else {
            exiting.delete(el);
            added.push(el);
          }
        }

        let nothingChanged = !added.length;
        for (let i = 0; i < prev.length; i++) {
          const el = prev[i]!;
          if (!nextSet.has(el)) {
            if (!exiting.has(el)) {
              removed.push(el);
              exiting.add(el);
            }
            handleRemoved(next, el, i);
          }
          if (nothingChanged && el !== next[i]) nothingChanged = false;
        }

        // skip if nothing changed
        if (!removed.length && nothingChanged) return prev;

        onChange({ list: next, added, removed, unchanged, finishRemoved });

        prevSet = nextSet;
        return next;
      });
    },
    options.appear ? [] : initSource.slice()
  );
}

export type ListTransitionOptions<T> = {
  /**
   * A function to be called when the list changes. {@link OnListChange}
   *
   * It receives the list of current, added, removed, and unchanged elements.
   * It also receives a callback to be called when the removed elements are finished animating (they can be removed from the DOM).
   */
  onChange: OnListChange<T>;
  /** whether to run the transition on the initial elements. Defaults to `false` */
  appear?: boolean;
  /**
   * This controls how the elements exit. {@link ExitMethod}
   * - `"remove"` removes the element immediately.
   * - `"move-to-end"` (default) will move elements which have exited to the end of the array.
   * - `"keep-index"` will splice them in at their previous index.
   */
  exitMethod?: ExitMethod;
};

export type OnListChange<T> = (payload: {
  /** full list of elements to be rendered */
  list: T[];
  /** list of elements that were added since the last change */
  added: T[];
  /** list of elements that were removed since the last change */
  removed: T[];
  /** list of elements that were already added before, and are not currently exiting */
  unchanged: T[];
  /** Callback for finishing the transition of exiting elements - removes them from rendered array */
  finishRemoved: (els: T[]) => void;
}) => void;

export type ExitMethod = 'remove' | 'move-to-end' | 'keep-index';
